import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { JWT_REFRESH_TIMER_THRESHOLD_SECONDS } from "../../helpers/constants";
import { useLocalStorage } from "../../helpers/hooks/useLocalStorage";
import usePusher from "../../helpers/hooks/usePusher";
import { useTicker } from "../../helpers/hooks/useTicker";
import { getEnvValueFor, parseJwt } from "../../helpers/utils";
import { authmsApi, rmsApi } from "../../state/axios";
import { authOperations } from "../../state/features/auth";
import AuthTokenError from "../authTokenError";
import { setIsAdminUser } from "../../state/features/company/slice";

interface IAuthProvideProps {
  children: React.ReactNode;
}

const AuthProvider = ({ children }: IAuthProvideProps) => {
  const [jwt, setJwt] = useLocalStorage("jwt", "");
  const [refreshToken] = useLocalStorage("rt", "");

  const { authorize, tokenExpiry, initiateTicker, isTokenExpired } =
    useSelector((state: any) => state.auth);
  const dispatch = useDispatch();
  const { authorizeUser, setJwtData } = authOperations;
  const { updateJwtForPusher } = usePusher();

  const decodeAuthDataFromJwt = (token: string) => {
    try {
      if (token) {
        const authData = parseJwt(token);
        console.debug(
          `[AUTH] Token parse success. expiry: ${authData.exp} seconds`
        );
        console.debug(
          `[AUTH] New token will expire on ${new Date(authData.exp * 1000)}`
        );
        setJwtData(authData);
        if (authData.is_admin) {
          dispatch(setIsAdminUser(true));
        }
      }
    } catch (error) {
      console.debug("[AUTH] Invalid Token");
      console.log(error);
      authorizeUser(false);
    }
  };

  const handleTokenRefresh = () => {
    console.debug("[AUTH] Refreshing JWT token");

    const demoSessionRefresh = () => {
      try {
        const payload = parseJwt(jwt);
        return Boolean(payload.demo_account && payload.demo_session_id);
      } catch {
        return false;
      }
    };

    if (demoSessionRefresh()) {
      rmsApi
        .post("/api/v1/demo-sessions/refresh")
        .then((response) => {
          console.debug("[AUTH] Demo session JWT refresh success");
          setJwt(response.data.jwt);
          if (response.data.refresh_token) {
            window.localStorage.setItem("rt", response.data.refresh_token);
          }
          updateJwtForPusher(response.data.jwt);
        })
        .catch((error) => {
          console.debug("[AUTH] Demo session JWT refresh failed");
          console.log(error);
          authorizeUser(false);
        });
      return;
    }

    authmsApi
      .post(
        `/api/dashboard/v1/tokens/refresh`,
        {
          jwt: jwt,
          refresh_token: refreshToken,
        },
        {
          baseURL: `${getEnvValueFor("AUTH_MS_BASE_URL")}`,
        }
      )
      .then((response) => {
        console.debug("[AUTH] JWT token refresh success");
        setJwt(response.data.jwt);
        updateJwtForPusher(jwt);
      })
      .catch((error) => {
        console.debug("[AUTH] JWT token refresh failed");
        console.log(error);
        authorizeUser(false);
      });
  };

  const [tickerStatus, startTicker, setTokenExpiry] = useTicker({
    thresholdValue: JWT_REFRESH_TIMER_THRESHOLD_SECONDS,
    onThresholdTrigger: handleTokenRefresh,
  });

  useEffect(() => {
    if (isTokenExpired) {
      handleTokenRefresh();
    }
  }, [isTokenExpired]);

  useEffect(() => {
    if (jwt) {
      decodeAuthDataFromJwt(jwt);
    } else {
      // If jwt is missing in localStorage
      authorizeUser(false);
    }
  }, [jwt]);

  useEffect(() => {
    if (initiateTicker && tokenExpiry) {
      setTokenExpiry(tokenExpiry); // Pass expiry value to ticker
      startTicker(true);
    }
  }, [initiateTicker, tokenExpiry]);

  return authorize ? <>{children}</> : <AuthTokenError />;
};

export default AuthProvider;
